import React from 'react';
import { Calendar as CalendarIcon, X, Lock, Check } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const BOOKED_STATUSES = ['confirmed', 'reserved'];

const BlockedView = ({ info, onUnblock, loading }) => (
  <div>
    <Badge className="bg-red-500 text-white mb-4">Blocked</Badge>
    <p className="text-sm text-[#8B7355]/70 mb-2">
      <strong>Reason:</strong> {info.reason}
    </p>
    <p className="text-sm text-[#8B7355]/70 mb-4">
      <strong>Blocked by:</strong> {info.blocked_by}
    </p>
    <Button
      onClick={() => onUnblock(info.date)}
      disabled={loading}
      className="w-full bg-green-600 hover:bg-green-700 text-white"
      data-testid="unblock-date-btn"
    >
      <Check className="w-4 h-4 mr-2" />
      Unblock Date
    </Button>
  </div>
);

const BookedView = ({ info }) => (
  <div>
    <Badge className="bg-[#C9A961] text-white mb-4">
      {info.status === 'confirmed' ? 'Confirmed' : 'Reserved'}
    </Badge>
    {info.inquiry && (
      <div className="space-y-2 text-sm">
        <p className="text-[#8B7355]"><strong>Name:</strong> {info.inquiry.name}</p>
        <p className="text-[#8B7355]"><strong>Email:</strong> {info.inquiry.email}</p>
        <p className="text-[#8B7355]"><strong>Phone:</strong> {info.inquiry.phone}</p>
        <p className="text-[#8B7355]"><strong>Event:</strong> {info.inquiry.event_type}</p>
        <p className="text-[#8B7355]"><strong>Package:</strong> {info.inquiry.package}</p>
      </div>
    )}
  </div>
);

const BlockForm = ({ blockReason, setBlockReason, onBlock, loading }) => (
  <div className="space-y-4">
    <p className="text-sm text-[#8B7355]/70">Block this date to prevent bookings</p>
    <div>
      <Label htmlFor="reason" className="text-[#8B7355]">Reason (optional)</Label>
      <Textarea
        id="reason"
        value={blockReason}
        onChange={(e) => setBlockReason(e.target.value)}
        placeholder="e.g., Personal event, maintenance, etc."
        className="border-[#8B7355]/30 mt-2"
        rows={3}
        data-testid="block-reason-input"
      />
    </div>
    <Button
      onClick={onBlock}
      disabled={loading}
      className="w-full bg-red-600 hover:bg-red-700 text-white"
      data-testid="block-date-btn"
    >
      <Lock className="w-4 h-4 mr-2" />
      Block Date
    </Button>
  </div>
);

const AvailableView = () => (
  <div className="text-center py-8">
    <CalendarIcon className="w-12 h-12 text-[#8B7355]/30 mx-auto mb-4" />
    <p className="text-[#8B7355]/70">This date is available</p>
  </div>
);

export const DateDetailPanel = ({
  selectedDate,
  selectedDateInfo,
  showBlockForm,
  blockReason,
  setBlockReason,
  loading,
  onBlock,
  onUnblock,
  onClose,
}) => {
  if (!selectedDate) return null;

  const renderBody = () => {
    if (selectedDateInfo && selectedDateInfo.status === 'blocked') {
      return <BlockedView info={selectedDateInfo} onUnblock={onUnblock} loading={loading} />;
    }
    if (selectedDateInfo && BOOKED_STATUSES.includes(selectedDateInfo.status)) {
      return <BookedView info={selectedDateInfo} />;
    }
    if (showBlockForm) {
      return (
        <BlockForm
          blockReason={blockReason}
          setBlockReason={setBlockReason}
          onBlock={onBlock}
          loading={loading}
        />
      );
    }
    return <AvailableView />;
  };

  return (
    <Card className="border-2 border-[#8B7355]/20">
      <CardHeader>
        <CardTitle className="font-serif text-xl text-[#8B7355] flex items-center justify-between">
          {selectedDate.toLocaleDateString()}
          {showBlockForm && (
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-block-form">
              <X className="w-4 h-4" />
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>{renderBody()}</CardContent>
    </Card>
  );
};
